import { useState, useEffect } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import { 
  Users, 
  DollarSign, 
  Copy, 
  Check, 
  Clock, 
  CheckCircle,
  ArrowUpRight,
  Search,
  RefreshCw,
  UserPlus,
  X,
  Loader2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminReferralCommissions = () => {
  const [codes, setCodes] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState({ total_pending: 0, total_paid: 0, pending_count: 0, paid_count: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [markingPaid, setMarkingPaid] = useState(false);
  
  // Modal state for creating VIP Partner
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [codesRes, commissionsRes] = await Promise.all([
        axios.get(`${API}/api/referrals/admin/all`, { headers }),
        axios.get(`${API}/api/referrals/admin/commissions${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`, { headers })
      ]);

      setCodes(codesRes.data.referral_codes || []);
      setCommissions(commissionsRes.data.commissions || []);
      setSummary(commissionsRes.data.summary || {});
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar datos de referidos");
    } finally {
      setLoading(false);
    }
  };

  // Search users for VIP Partner creation
  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/admin/users?search=${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out users who are already VIP Partners
      const existingVipIds = codes.map(c => c.user_id);
      const filtered = (response.data || []).filter(u => 
        !existingVipIds.includes(u.id) && u.role !== "admin"
      );
      setSearchResults(filtered.slice(0, 10));
    } catch (error) {
      console.error(error);
    } finally {
      setSearching(false);
    }
  };

  // Create VIP Partner
  const createVipPartner = async (userId, userName) => {
    setCreating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/referrals/admin/create-vip-partner/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${userName} ahora es VIP Partner con código ${response.data.code}`);
      setShowModal(false);
      setSearchQuery("");
      setSearchResults([]);
      fetchData(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear VIP Partner");
    } finally {
      setCreating(false);
    }
  };

  const handleSelectCommission = (id) => {
    setSelectedCommissions(prev => 
      prev.includes(id) 
        ? prev.filter(c => c !== id)
        : [...prev, id]
    );
  };

  const handleSelectAllPending = () => {
    const pendingIds = commissions.filter(c => c.status === "pending").map(c => c.id);
    setSelectedCommissions(pendingIds);
  };

  const handleMarkPaid = async () => {
    if (selectedCommissions.length === 0) {
      toast.error("Selecciona al menos una comisión");
      return;
    }

    const reference = prompt("Referencia de pago (opcional):");
    
    setMarkingPaid(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/referrals/admin/mark-paid`,
        { commission_ids: selectedCommissions, payment_reference: reference },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedCommissions.length} comisiones marcadas como pagadas`);
      setSelectedCommissions([]);
      fetchData();
    } catch (error) {
      toast.error("Error al marcar como pagadas");
    } finally {
      setMarkingPaid(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('es-MX', { 
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Comisiones por Referidos</h1>
            <p className="text-gray-500 mt-1">Gestiona las comisiones de VIP Partners</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus className="w-4 h-4 mr-2" />
              Crear VIP Partner
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Actualizar
          </Button>
          </div>
        </div>

        {/* Modal: Create VIP Partner */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Crear VIP Partner</h2>
                <button 
                  onClick={() => { setShowModal(false); setSearchQuery(""); setSearchResults([]); }}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-4">
                  Busca un cliente existente para convertirlo en VIP Partner. 
                  Obtendrá un código de referido y podrá ganar comisiones.
                </p>
                
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Buscar por nombre o email..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchUsers(e.target.value);
                    }}
                    className="pl-10"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                  )}
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  {searchResults.length === 0 && searchQuery.length >= 2 && !searching && (
                    <p className="text-center text-gray-500 py-4">No se encontraron clientes</p>
                  )}
                  
                  {searchResults.map(user => (
                    <div 
                      key={user.id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg border border-gray-100 mb-2"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mt-1 inline-block">
                          {user.plan_type || "Sin plan"}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => createVipPartner(user.id, user.name)}
                        disabled={creating}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {creating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-1" />
                            Crear
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-700">
                    <strong>Comisiones VIP Partner:</strong><br/>
                    • Plan Estándar ($19.99): 90% = $18.00<br/>
                    • Plan Premium ($49.99): 50% = $25.00<br/>
                    • Plan VIP ($99.99): 50% = $49.99
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pendiente de Pago</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(summary.total_pending)}</p>
                <p className="text-xs text-gray-400">{summary.pending_count} comisiones</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Pagado</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.total_paid)}</p>
                <p className="text-xs text-gray-400">{summary.paid_count} comisiones</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">VIP Partners</p>
                <p className="text-2xl font-bold text-blue-600">{codes.length}</p>
                <p className="text-xs text-gray-400">con código activo</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ArrowUpRight className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Referidos</p>
                <p className="text-2xl font-bold text-purple-600">{commissions.length}</p>
                <p className="text-xs text-gray-400">ventas por código</p>
              </div>
            </div>
          </div>
        </div>

        {/* VIP Partners / Codes Section */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">VIP Partners y sus Códigos</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referidos</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pendiente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pagado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {codes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      No hay VIP Partners registrados
                    </td>
                  </tr>
                ) : (
                  codes.map(code => (
                    <tr key={code.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{code.user_name}</p>
                          <p className="text-sm text-gray-500">{code.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">{code.code}</code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(code.code)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-900">{code.total_referrals}</td>
                      <td className="px-4 py-3 text-amber-600 font-medium">{formatCurrency(code.pending_balance)}</td>
                      <td className="px-4 py-3 text-emerald-600">{formatCurrency(code.paid_balance)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Commissions Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Historial de Comisiones</h2>
            <div className="flex items-center gap-3">
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              >
                <option value="all">Todos</option>
                <option value="pending">Pendientes</option>
                <option value="paid">Pagadas</option>
              </select>
              {selectedCommissions.length > 0 && (
                <Button 
                  onClick={handleMarkPaid}
                  disabled={markingPaid}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  size="sm"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Marcar Pagadas ({selectedCommissions.length})
                </Button>
              )}
              {statusFilter !== "paid" && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleSelectAllPending}
                >
                  Seleccionar Pendientes
                </Button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referido</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Venta</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comisión</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {commissions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No hay comisiones registradas
                    </td>
                  </tr>
                ) : (
                  commissions.map(commission => (
                    <tr key={commission.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {commission.status === "pending" && (
                          <input 
                            type="checkbox"
                            checked={selectedCommissions.includes(commission.id)}
                            onChange={() => handleSelectCommission(commission.id)}
                            className="w-4 h-4 text-emerald-600 rounded"
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{commission.referred_user_name}</p>
                          <p className="text-sm text-gray-500">{commission.referred_user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{commission.product_name}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(commission.sale_amount)}</td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-emerald-600">{formatCurrency(commission.commission_amount)}</span>
                        <span className="text-xs text-gray-400 ml-1">({Math.round(commission.commission_rate * 100)}%)</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-gray-700">{commission.referrer_name}</p>
                          <code className="text-xs bg-gray-100 px-1 rounded">{commission.referral_code}</code>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {commission.status === "pending" ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <Clock className="w-3 h-3 mr-1" />
                            Pendiente
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Pagada
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(commission.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminReferralCommissions;
